import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Card, { CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import PricingSettings from '../components/admin/PricingSettings';
import './AdminPage.css';

const ADMIN_PASSWORD = 'vibelink2024'; // In production, use env variable

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [activeTab, setActiveTab] = useState('briefs');
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [stats, setStats] = useState({ total: 0, thisWeek: 0, thisMonth: 0 });

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      sessionStorage.setItem('adminAuth', 'true');
    } else {
      setAuthError('Incorrect password');
    }
  };

  useEffect(() => {
    // Check if already authenticated
    if (sessionStorage.getItem('adminAuth') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'briefs') {
      fetchBriefs();
    }
  }, [isAuthenticated, activeTab]);

  const fetchBriefs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/briefs');
      if (!res.ok) throw new Error('Failed to fetch briefs');
      const data = await res.json();
      setBriefs(data.briefs || []);
      calculateStats(data.briefs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (briefsData) => {
    const now = new Date();
    const oneWeekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);

    const thisWeek = briefsData.filter(b => new Date(b.createdAt) >= oneWeekAgo).length;
    const thisMonth = briefsData.filter(b => new Date(b.createdAt) >= oneMonthAgo).length;

    setStats({
      total: briefsData.length,
      thisWeek,
      thisMonth
    });
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      const res = await fetch(`/api/brief/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete brief');
      setBriefs(briefs.filter(b => b.id !== id));
      calculateStats(briefs.filter(b => b.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
  };

  const filteredBriefs = briefs
    .filter(brief => {
      const term = searchTerm.toLowerCase();
      return (
        brief.brandCore?.name?.toLowerCase().includes(term) ||
        brief.brandCore?.mission?.toLowerCase().includes(term) ||
        brief.id.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      let aVal, bVal;
      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aVal = new Date(a[sortBy]);
        bVal = new Date(b[sortBy]);
      } else if (sortBy === 'name') {
        aVal = a.brandCore?.name?.toLowerCase() || '';
        bVal = b.brandCore?.name?.toLowerCase() || '';
      }
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-page admin-page--login">
        <div className="admin-login">
          <div className="admin-login__header">
            <Link to="/" className="admin-login__logo">
              <svg viewBox="0 0 100 100" className="admin-login__logo-icon">
                <defs>
                  <linearGradient id="adminVibeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#818cf8' }} />
                    <stop offset="50%" style={{ stopColor: '#c084fc' }} />
                    <stop offset="100%" style={{ stopColor: '#f472b6' }} />
                  </linearGradient>
                </defs>
                <rect width="100" height="100" rx="20" fill="var(--bg-secondary)" />
                <path 
                  d="M25 70 L40 30 L50 55 L60 30 L75 70" 
                  stroke="url(#adminVibeGrad)" 
                  strokeWidth="8" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  fill="none"
                />
                <circle cx="50" cy="75" r="6" fill="url(#adminVibeGrad)" />
              </svg>
            </Link>
            <h1>Admin Access</h1>
            <p>Enter the admin password to continue</p>
          </div>
          <form onSubmit={handleLogin} className="admin-login__form">
            <Input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={authError}
            />
            <Button type="submit" variant="primary" size="lg">
              Login
            </Button>
          </form>
          <Link to="/" className="admin-login__back">← Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-page__header">
        <div className="admin-page__header-left">
          <Link to="/" className="admin-page__logo">
            <svg viewBox="0 0 100 100" className="admin-page__logo-icon">
              <defs>
                <linearGradient id="adminHeaderGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#818cf8' }} />
                  <stop offset="50%" style={{ stopColor: '#c084fc' }} />
                  <stop offset="100%" style={{ stopColor: '#f472b6' }} />
                </linearGradient>
              </defs>
              <rect width="100" height="100" rx="20" fill="var(--bg-secondary)" />
              <path 
                d="M25 70 L40 30 L50 55 L60 30 L75 70" 
                stroke="url(#adminHeaderGrad)" 
                strokeWidth="8" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                fill="none"
              />
              <circle cx="50" cy="75" r="6" fill="url(#adminHeaderGrad)" />
            </svg>
            <span>Vibe Link</span>
          </Link>
          <span className="admin-page__badge">Admin</span>
        </div>
        <div className="admin-page__header-right">
          <Button variant="secondary" size="sm" onClick={handleLogout}>
            Logout
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'briefs' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('briefs')}
        >
          📋 Briefs
        </button>
        <button 
          className={`admin-tab ${activeTab === 'pricing' ? 'admin-tab--active' : ''}`}
          onClick={() => setActiveTab('pricing')}
        >
          💰 Pricing
        </button>
      </div>

      <main className="admin-page__main">
        {activeTab === 'briefs' && (
          <>
            {/* Stats Cards */}
            <div className="admin-stats">
              <Card className="admin-stat-card">
                <CardContent>
                  <div className="admin-stat">
                    <span className="admin-stat__value">{stats.total}</span>
                    <span className="admin-stat__label">Total Briefs</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="admin-stat-card">
                <CardContent>
                  <div className="admin-stat">
                    <span className="admin-stat__value">{stats.thisWeek}</span>
                    <span className="admin-stat__label">This Week</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="admin-stat-card">
                <CardContent>
                  <div className="admin-stat">
                    <span className="admin-stat__value">{stats.thisMonth}</span>
                    <span className="admin-stat__label">This Month</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Controls */}
            <div className="admin-controls">
              <div className="admin-search">
                <input
                  type="text"
                  placeholder="Search by name, mission, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="admin-search__input"
                />
                {searchTerm && (
                  <button 
                    className="admin-search__clear"
                    onClick={() => setSearchTerm('')}
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="admin-sort">
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="admin-sort__select"
                >
                  <option value="createdAt">Created Date</option>
                  <option value="updatedAt">Updated Date</option>
                  <option value="name">Name</option>
                </select>
                <button 
                  className="admin-sort__order"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
              <Button variant="ghost" size="sm" onClick={fetchBriefs}>
                ↻ Refresh
              </Button>
            </div>

            {/* Error State */}
            {error && (
              <div className="admin-error">
                <p>{error}</p>
                <Button variant="secondary" size="sm" onClick={fetchBriefs}>
                  Try Again
                </Button>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="admin-loading">
                <div className="admin-loading__spinner" />
                <p>Loading briefs...</p>
              </div>
            )}

            {/* Briefs Table */}
            {!loading && !error && (
              <>
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Brand Name</th>
                        <th>Mission</th>
                        <th>Color</th>
                        <th>Mode</th>
                        <th>Font</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBriefs.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="admin-table__empty">
                            {searchTerm ? 'No briefs match your search' : 'No briefs yet'}
                          </td>
                        </tr>
                      ) : (
                        filteredBriefs.map((brief) => (
                          <tr key={brief.id}>
                            <td>
                              <div className="admin-table__brand">
                                <span 
                                  className="admin-table__logo"
                                  style={{ backgroundColor: brief.visuals?.colorSeed }}
                                >
                                  {brief.brandCore?.name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                                <span className="admin-table__name">
                                  {brief.brandCore?.name || 'Untitled'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className="admin-table__mission">
                                {brief.brandCore?.mission?.substring(0, 60) || '—'}
                                {brief.brandCore?.mission?.length > 60 ? '...' : ''}
                              </span>
                            </td>
                            <td>
                              <div className="admin-table__color">
                                <span 
                                  className="admin-table__color-swatch"
                                  style={{ backgroundColor: brief.visuals?.colorSeed }}
                                />
                                <code>{brief.visuals?.colorSeed?.toUpperCase()}</code>
                              </div>
                            </td>
                            <td>
                              <span className="admin-table__mode">
                                {brief.visuals?.colorMode === 'dark' ? '🌙' : '☀️'}
                              </span>
                            </td>
                            <td>
                              <span className="admin-table__font">
                                {brief.visuals?.fontStyle}
                              </span>
                            </td>
                            <td>
                              <span className="admin-table__date">
                                {formatDate(brief.createdAt)}
                              </span>
                            </td>
                            <td>
                              <div className="admin-table__actions">
                                <Link 
                                  to={`/brief/${brief.id}`}
                                  className="admin-table__action admin-table__action--view"
                                  title="View"
                                >
                                  👁
                                </Link>
                                <button
                                  className="admin-table__action admin-table__action--delete"
                                  onClick={() => setDeleteConfirm(brief.id)}
                                  title="Delete"
                                >
                                  🗑
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                
                <div className="admin-footer">
                  <p>Showing {filteredBriefs.length} of {briefs.length} briefs</p>
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'pricing' && (
          <PricingSettings />
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="admin-modal-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Brief?</h3>
              <p>This action cannot be undone. The brief will be permanently deleted.</p>
              <div className="admin-modal__actions">
                <Button 
                  variant="secondary" 
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={() => handleDelete(deleteConfirm)}
                  loading={deleting}
                  className="admin-modal__delete-btn"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
